// =============================================================================
// io/sync/importBatches.ts — import history, and undoing one.
// -----------------------------------------------------------------------------
// ── 🚨 BATCHES ARE DERIVED, NOT STORED ──
//
// There is no `import_batches` table and there should not be. Every trade an
// import wrote carries `importBatchId` / `importedAt` / `importFilename` /
// `importFormat` in its `extras`, and a batch is what you get by grouping on the
// first of those. Beta's own comment says it outright: "Batches are DERIVED by
// grouping the stamped trades — no separate table."
//
// That is not a shortcut, it is the property that makes undo correct. A stored
// batch row can disagree with the trades it claims — a trade deleted by hand, a
// batch row orphaned by a failed insert — and an undo driven from the row then
// deletes the wrong set or reports a count nobody can reconcile. Grouping cannot
// drift, because the trades ARE the record.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { PAGE_ROWS } from './load';
import { reportFailureAs } from '../diagnostics/errorLog';

/** One import, reconstructed from the trades it wrote. */
export interface ImportBatchSummary {
  id: string;
  filename: string;
  /** Epoch millis for sorting and display. 0 when unparseable or absent. */
  importedAt: number;
  format: string;
  tradeIds: string[];
  /** Distinct account ids the batch landed in. */
  accountIds: string[];
}

interface BatchRow {
  id: string;
  accountId: string | null;
  batchId: string | null;
  importedAt: unknown;
  filename: unknown;
  format: unknown;
}

/**
 * ⚠ Six fields, four of them jsonb SUB-PATHS. Same annex technique as
 * `executions` on the table read (D83) and for the same reason: these are
 * `extras` keys the typed model does not own, so they are read by path and never
 * added to `Trade`. Reading the whole `extras` blob for every trade in the book
 * to reach four keys would be the fat version of this.
 */
const BATCH_SELECT = [
  'id',
  // ⚠ ALIASED. PostgREST returns the column's own name, so an unaliased
  // `account_id` arrives under that key while this module reads `accountId` —
  // and the account list silently renders "—" on every batch. Caught by looking
  // at the rendered history against a real import, not by the read succeeding.
  'accountId:account_id',
  'batchId:extras->>importBatchId',
  'importedAt:extras->importedAt',
  'filename:extras->>importFilename',
  'format:extras->>importFormat',
].join(',');

/**
 * Every tracked import, newest first.
 *
 * ⚠ WHOLE BOOK, and unavoidably so: an import from March is still in the history
 * in July, and scoping this to the active range would show a history that
 * changes when the date filter moves — which reads as imports disappearing.
 *
 * ⚠ Filtered SERVER-SIDE on `extras ? 'importBatchId'`, so trades that predate
 * batch stamping never cross the wire. On a book where only recent imports are
 * stamped that is most of it.
 */
export async function loadImportBatches(sb: SupabaseClient): Promise<ImportBatchSummary[]> {
  const rows: BatchRow[] = [];

  for (let from = 0; ; from += PAGE_ROWS) {
    const { data, error } = await sb
      .from('trades')
      .select(BATCH_SELECT)
      .not('extras->importBatchId', 'is', null)
      .order('id', { ascending: true })
      .range(from, from + PAGE_ROWS - 1);

    // ⚠ Throws rather than returning a short history. A batch missing from the
    // list is one the user cannot undo, and it looks identical to an import that
    // was never tracked.
    if (error) {
      throw new Error(`import history read failed on page ${(from / PAGE_ROWS) + 1}: ${error.message}`);
    }
    if (!data || !data.length) break;
    rows.push(...(data as unknown as BatchRow[]));
    if (data.length < PAGE_ROWS) break;
  }

  return groupImportBatches(rows);
}

/**
 * `importedAt` → epoch millis, accepting BOTH shapes.
 *
 * ⚠ Beta writes an ISO string (`new Date().toISOString()`), and Josh's book
 * already contains batches stamped that way. A `Number()` cast alone yields NaN
 * for every one of them, which `|| 0` then turns into "no timestamp" — the whole
 * pre-existing history sorted last, every date rendered as an em-dash, and
 * nothing that looks like an error.
 *
 * A numeric form is accepted too so a stamp written before this was settled
 * still reads. Neither client should be writing millis now.
 */
function toEpoch(v: unknown): number {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v) {
    const n = Date.parse(v);
    if (Number.isFinite(n)) return n;
    // A numeric string, which is what a jsonb number round-trips to in some
    // PostgREST versions.
    const asNum = Number(v);
    if (Number.isFinite(asNum)) return asNum;
  }
  return 0;
}

/** Pure, so the grouping is testable without a client. */
export function groupImportBatches(rows: readonly BatchRow[]): ImportBatchSummary[] {
  const byId = new Map<string, ImportBatchSummary>();

  for (const r of rows) {
    const id = r.batchId;
    if (!id) continue;
    let b = byId.get(id);
    if (!b) {
      b = {
        id,
        filename: String(r.filename ?? '') || 'import',
        importedAt: toEpoch(r.importedAt),
        format: String(r.format ?? ''),
        tradeIds: [],
        accountIds: [],
      };
      byId.set(id, b);
    }
    b.tradeIds.push(String(r.id));
    const acc = r.accountId;
    if (acc && !b.accountIds.includes(acc)) b.accountIds.push(acc);
  }

  // ⚠ Newest first, and the tiebreak is the id rather than nothing: two imports
  // in the same millisecond would otherwise order by Map insertion, which is the
  // page order — stable, but arbitrary and different after a re-read.
  return [...byId.values()].sort((a, b) =>
    (b.importedAt - a.importedAt) || a.id.localeCompare(b.id));
}

export interface UndoResult {
  deleted: number;
  error: string | null;
}

/**
 * Delete every trade in one batch.
 *
 * ── 🚨 THE ONLY DESTRUCTIVE WRITE IN THIS CLIENT ──
 *
 * ⚠ Deletes BY TRADE ID, from the list the caller was shown — not by re-querying
 * `extras->importBatchId`. The user confirmed a specific count against a specific
 * list, and a delete that re-derives its own target can remove more than was
 * agreed to if anything changed in between. The ids are the consent.
 *
 * ⚠ Chunked, and a partial delete is REPORTED. Half a batch removed is a book in
 * a state nobody chose, and the user needs to know which half to look at rather
 * than assume the undo did nothing.
 *
 * ⚠ This takes annotations with it. A trade imported and then reviewed carries
 * notes, a grade and a pattern; undoing the import deletes all of it. The caller
 * must say so before asking — see the confirmation copy in the UI.
 */
export async function undoImportBatch(
  sb: SupabaseClient,
  tradeIds: readonly string[],
): Promise<UndoResult> {
  if (!tradeIds.length) return { deleted: 0, error: null };

  const CHUNK = 200;
  let deleted = 0;

  for (let i = 0; i < tradeIds.length; i += CHUNK) {
    const slice = tradeIds.slice(i, i + CHUNK);
    const { error } = await sb.from('trades').delete().in('id', slice as string[]);

    if (error) {
      return {
        deleted,
        // ⚠ The count is in the logged line too. A half-finished delete is a book
        // in a state nobody chose, and "how far did it get" is the first question.
        error: reportFailureAs(
          'undo import',
          error,
          `Undo failed after removing ${deleted} of ${tradeIds.length} trade`
          + `${tradeIds.length === 1 ? '' : 's'}: ${error.message}. The rest are still in `
          + `your book — re-run the undo to finish, or leave it and the batch stays partial.`,
        ),
      };
    }
    deleted += slice.length;
  }

  return { deleted, error: null };
}
